import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControl, IconButton, InputLabel, MenuItem,
  Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Typography, styled,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSelector } from 'react-redux';
import { axiosInstance } from '../../../Axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const PageShell = styled(Box)(() => ({
  minHeight: 'calc(100vh - 120px)', padding: 24, borderRadius: 8,
  background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%)',
}));

const HeroPanel = styled(Paper)(() => ({
  position: 'relative', overflow: 'hidden', borderRadius: 8, padding: 24, color: '#fff',
  background: 'linear-gradient(135deg, #111827, #334155)',
  boxShadow: '0 22px 48px rgba(15, 23, 42, .18)',
  '&::after': {
    content: '""', position: 'absolute', right: -40, bottom: -70, width: 270, height: 155,
    background: 'repeating-linear-gradient(-45deg, #f59e0b 0 16px, #111827 16px 32px)',
    transform: 'rotate(-6deg)', opacity: .9,
  },
}));

const getToken = (loginInfo) => loginInfo?.token || loginInfo?.result?.userToken;
const unwrap = (response) => response?.data?.data || response?.data?.result || response?.data?.projectPlanCalendar || response?.data || [];
const asArray = (value) => Array.isArray(value) ? value : [];
const valueOf = (row, keys, fallback = '-') => {
  const key = keys.find((item) => row?.[item] !== undefined && row?.[item] !== null && row?.[item] !== '');
  return key ? row[key] : fallback;
};
const projectIdOf = (row) => valueOf(row, ['projectId', 'ProjectId', 'id'], '');
const projectPlanIdOf = (row) => valueOf(row, ['projectPlanId', 'ProjectPlanId'], '');
const projectNameOf = (row) => valueOf(row, ['projectName', 'ProjectName', 'name'], 'Unnamed project');
const planNameOf = (row) => valueOf(row, ['planTitle', 'PlanTitle', 'title'], 'Unnamed plan');
const formatDate = (value) => {
  if (!value || value === '-') return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-GB');
};
const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
const dateKeyOf = (value) => {
  const date = parseDate(value);
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const calendarDateOf = (row) => valueOf(row, ['calendarDate', 'CalendarDate', 'actualDate', 'ActualDate', 'planDate', 'PlanDate'], '');
const isHolidayRow = (row) => Boolean(
  row?.isHoliday || row?.IsHoliday || row?.holidayId || row?.HolidayId || row?.holidayName || row?.HolidayName
);
const isPlannedRow = (row) => Boolean(
  row?.planDayId || row?.PlanDayId || row?.dayTitle || row?.DayTitle || row?.dayNo || row?.DayNo
);
const getCalendarDays = (month, rowsByDate) => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(month.getFullYear(), month.getMonth(), 1 - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start); date.setDate(start.getDate() + index);
    const dateKey = dateKeyOf(date);
    return { date, dateKey, inMonth: date.getMonth() === month.getMonth(), data: rowsByDate[dateKey] };
  });
};
const columns = [
  ['dayNo', 'Day no.'], ['dayTitle', 'Day plan'], ['planDate', 'Plan date', 'date'],
  ['taskCount', 'Tasks'], ['labourCount', 'Labour'], ['materialCount', 'Materials'],
  ['subcontractorCount', 'Subcontractors'], ['status', 'Status'],
];

const normalizeDay = (row, index) => ({
  id: valueOf(row, ['planDayId', 'projectPlanDayId', 'id'], index),
  projectId: projectIdOf(row), projectPlanId: projectPlanIdOf(row),
  dayNo: valueOf(row, ['dayNo', 'DayNo']),
  dayTitle: valueOf(row, ['dayTitle', 'DayTitle', 'title'], 'Planned work'),
  planDate: valueOf(row, ['calendarDate', 'CalendarDate', 'actualDate', 'ActualDate', 'planDate', 'PlanDate', 'workDate', 'date']),
  taskCount: valueOf(row, ['taskCount', 'TaskCount'], row.tasks?.length ?? 0),
  labourCount: valueOf(row, ['labourCount', 'LabourCount'], row.labours?.length ?? 0),
  materialCount: valueOf(row, ['materialCount', 'MaterialCount'], row.materials?.length ?? 0),
  subcontractorCount: valueOf(row, ['subcontractorCount', 'SubcontractorCount'], row.subcontractors?.length ?? 0),
  status: valueOf(row, ['statusName', 'status'], 'Planned'),
});

export default function DayPlanReport() {
  const { loginInfo } = useSelector((state) => state.user);
  const [projects, setProjects] = useState([]);
  const [plans, setPlans] = useState([]);
  const [days, setDays] = useState([]);
  const [calendarRows, setCalendarRows] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [projectPlanId, setProjectPlanId] = useState('');
  const [loading, setLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    let active = true;
    const token = getToken(loginInfo);
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const load = async () => {
      setLoading(true); setError('');
      try {
        const [projectResponse, planResponse, dayResponse] = await Promise.all([
          axiosInstance.get('/Project/GetProjectList', { ...config, params: { Offset: 0, RecordCount: 1000, ProjectName: '' } }),
          axiosInstance.get('/ProjectPlan/GetProjectPlanList', { ...config, params: { ProjectPlanId: 0, ProjectId: 0, PlanTitle: '', Offset: 0, RecordCount: 5000 } }),
          axiosInstance.get('/ProjectPlanDay/GetProjectPlanDayList', { ...config, params: { ProjectId: 0, ProjectPlanId: 0, Offset: 0, RecordCount: 5000 } }),
        ]);
        if (!active) return;
        setProjects(asArray(unwrap(projectResponse)));
        setPlans(asArray(unwrap(planResponse)));
        setDays(asArray(unwrap(dayResponse)).map(normalizeDay));
      } catch (requestError) {
        if (active) setError(requestError?.response?.data?.message || requestError.message || 'Unable to load day plan report.');
      } finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [loginInfo, refreshKey]);

  useEffect(() => {
    let active = true;
    if (!projectId || !projectPlanId) { setCalendarRows([]); return undefined; }
    const loadCalendar = async () => {
      setCalendarLoading(true); setError('');
      try {
        const token = getToken(loginInfo);
        const response = await axiosInstance.get('/ProjectPlan/GetProjectPlanCalendar', {
          headers: { Authorization: `Bearer ${token}` }, params: { ProjectId: projectId, ProjectPlanId: projectPlanId },
        });
        if (!active) return;
        const payload = response?.data;
        const result = asArray(
          payload?.projectPlanCalendar || payload?.data?.projectPlanCalendar || payload?.result?.projectPlanCalendar ||
          payload?.data || payload?.result || payload
        );
        setCalendarRows(result);
        const firstDatedRow = result.find((row) => calendarDateOf(row));
        const firstDate = parseDate(calendarDateOf(firstDatedRow));
        if (firstDate) setCurrentMonth(new Date(firstDate.getFullYear(), firstDate.getMonth(), 1));
      } catch (requestError) {
        if (active) setError(requestError?.response?.data?.message || requestError.message || 'Unable to load the calendar.');
      } finally { if (active) setCalendarLoading(false); }
    };
    loadCalendar();
    return () => { active = false; };
  }, [loginInfo, projectId, projectPlanId, refreshKey]);

  const filteredPlans = useMemo(() => plans.filter((plan) => !projectId || String(projectIdOf(plan)) === String(projectId)), [plans, projectId]);
  const rows = useMemo(() => days.filter((day) => (
    (!projectId || String(day.projectId) === String(projectId)) &&
    (!projectPlanId || String(day.projectPlanId) === String(projectPlanId))
  )), [days, projectId, projectPlanId]);
  const selectedProject = projects.find((project) => String(projectIdOf(project)) === String(projectId));
  const selectedPlan = plans.find((plan) => String(projectPlanIdOf(plan)) === String(projectPlanId));
  const rowsByDate = useMemo(() => calendarRows.reduce((result, row) => {
    const key = dateKeyOf(calendarDateOf(row));
    if (key) result[key] = { ...(result[key] || {}), ...row };
    return result;
  }, {}), [calendarRows]);
  const calendarDays = useMemo(() => getCalendarDays(currentMonth, rowsByDate), [currentMonth, rowsByDate]);

  const handleProjectChange = (event) => {
    setProjectId(event.target.value);
    setProjectPlanId('');
  };

  const createPdf = () => {
    if (!projectId || !projectPlanId) { setError('Select a project and project plan before creating the PDF.'); return; }
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const projectName = projectNameOf(selectedProject);
    const planName = planNameOf(selectedPlan);
    const generatedAt = new Date();
    const documentRef = `CM-DAYPLAN-${dateKeyOf(generatedAt).replace(/-/g, '')}`;
    const totalTasks = rows.reduce((total, row) => total + Number(row.taskCount || 0), 0);
    const totalLabour = rows.reduce((total, row) => total + Number(row.labourCount || 0), 0);
    const totalMaterials = rows.reduce((total, row) => total + Number(row.materialCount || 0), 0);

    const drawHeader = (subtitle) => {
      doc.setFillColor(15, 23, 42); doc.rect(0, 0, pageWidth, 38, 'F');
      doc.setFillColor(245, 158, 11); doc.rect(0, 35, pageWidth, 3, 'F');
      doc.setFillColor(245, 158, 11); doc.roundedRect(margin, 9, 17, 17, 2, 2, 'F');
      doc.setTextColor(17, 24, 39); doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.text('CM', margin + 4, 20);
      doc.setTextColor(245, 158, 11); doc.setFontSize(8); doc.text('CONSTRUCTION MANAGEMENT', margin + 23, 12);
      doc.setTextColor(255, 255, 255); doc.setFontSize(20); doc.text('DAY PLAN REPORT', margin + 23, 22);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(203, 213, 225); doc.text(subtitle, pageWidth - margin, 10, { align: 'right' });
      doc.text(`Document: ${documentRef}`, pageWidth - margin, 16, { align: 'right' });
      doc.text(`Generated: ${generatedAt.toLocaleString('en-GB')}`, pageWidth - margin, 22, { align: 'right' });
    };

    drawHeader('Detailed execution register');
    doc.setTextColor(15, 23, 42); doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.text(projectName, margin, 49);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139); doc.text(planName, margin, 55);

    const metrics = [
      ['PLAN DAYS', rows.length, [245, 158, 11]], ['TOTAL TASKS', totalTasks, [37, 99, 235]],
      ['LABOUR', totalLabour, [5, 150, 105]], ['MATERIALS', totalMaterials, [147, 51, 234]],
    ];
    const metricWidth = 41;
    metrics.forEach(([label, value, color], index) => {
      const x = pageWidth - margin - ((metrics.length - index) * (metricWidth + 3));
      doc.setFillColor(248, 250, 252); doc.roundedRect(x, 43, metricWidth, 15, 2, 2, 'F');
      doc.setFillColor(...color); doc.rect(x, 43, 2, 15, 'F');
      doc.setTextColor(...color); doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text(String(value), x + 6, 50);
      doc.setTextColor(100, 116, 139); doc.setFontSize(6.5); doc.text(label, x + 6, 55);
    });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(15, 23, 42); doc.text('DAY PLAN EXECUTION DETAILS', margin, 65);

    autoTable(doc, {
      startY: 69,
      head: [columns.map(([, label]) => label)],
      body: rows.length
        ? rows.map((row) => columns.map(([key, , type]) => type === 'date' ? formatDate(row[key]) : String(row[key] ?? '-')))
        : [['No day-plan records available.', ...columns.slice(1).map(() => '')]],
      theme: 'grid', margin: { top: 18, left: margin, right: margin, bottom: 15 },
      styles: { font: 'helvetica', fontSize: 7, cellPadding: 2.5, textColor: [51, 65, 85], lineColor: [226, 232, 240], lineWidth: .15, overflow: 'linebreak' },
      headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 0: { cellWidth: 16 }, 1: { cellWidth: 55 }, 2: { cellWidth: 24 } },
    });
    const pages = doc.getNumberOfPages();
    for (let page = 1; page <= pages; page += 1) {
      doc.setPage(page);
      if (page > 1) {
        doc.setFillColor(15, 23, 42); doc.rect(0, 0, pageWidth, 12, 'F');
        doc.setTextColor(245, 158, 11); doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.text('CONSTRUCTION MANAGEMENT', margin, 7.5);
        doc.setTextColor(255, 255, 255); doc.text('DAY PLAN REPORT', pageWidth - margin, 7.5, { align: 'right' });
      }
      doc.setDrawColor(226, 232, 240); doc.line(margin, pageHeight - 10, pageWidth - margin, pageHeight - 10);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(148, 163, 184);
      doc.text(`${documentRef}  ·  Confidential`, margin, pageHeight - 5);
      doc.text(`Page ${page} of ${pages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
    }
    const safeProject = projectName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'project';
    const safePlan = planName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'plan';
    doc.save(`${safeProject}-${safePlan}-day-plan-${dateKeyOf(new Date())}.pdf`);
  };

  return <PageShell>
    <HeroPanel sx={{ mb: 3 }}><Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
      <Box><Typography variant="overline" sx={{ color: '#fbbf24', fontWeight: 900 }}>Daily site execution</Typography><Typography variant="h4" fontWeight={900} sx={{ color: '#fbbf24' }}>Day Plan Report</Typography><Typography sx={{ color: 'rgba(255,255,255,.72)', mt: 1 }}>Select a project and project plan to review daily work and create its PDF report.</Typography></Box>
      <Button variant="contained" startIcon={<PictureAsPdfIcon />} disabled={loading || !projectId || !projectPlanId} onClick={createPdf} sx={{ color: '#111827', fontWeight: 900, background: 'linear-gradient(135deg,#fbbf24,#f97316)' }}>Download PDF</Button>
    </Stack></HeroPanel>
    <Paper sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2} sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
        <Stack direction="row" spacing={1.5} alignItems="center"><EventNoteIcon sx={{ color: '#d97706' }} /><Box><Typography variant="h6" fontWeight={900}>Day Plan Details</Typography><Typography variant="body2" color="text.secondary">Filter by project and its project plan</Typography></Box></Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <FormControl size="small" sx={{ minWidth: 230 }}><InputLabel>Project</InputLabel><Select label="Project" value={projectId} onChange={handleProjectChange}><MenuItem value="">Select project</MenuItem>{projects.map((project) => <MenuItem key={projectIdOf(project)} value={projectIdOf(project)}>{projectNameOf(project)}</MenuItem>)}</Select></FormControl>
          <FormControl size="small" disabled={!projectId} sx={{ minWidth: 260 }}><InputLabel>Project Plan</InputLabel><Select label="Project Plan" value={projectPlanId} onChange={(event) => setProjectPlanId(event.target.value)}><MenuItem value="">Select project plan</MenuItem>{filteredPlans.map((plan) => <MenuItem key={projectPlanIdOf(plan)} value={projectPlanIdOf(plan)}>{planNameOf(plan)}</MenuItem>)}</Select></FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => setRefreshKey((key) => key + 1)}>Refresh</Button><Chip label={`${rows.length} days`} sx={{ fontWeight: 800, bgcolor: '#ffedd5', color: '#9a3412' }} />
        </Stack>
      </Stack>
      {error && <Alert severity="warning" onClose={() => setError('')} sx={{ borderRadius: 0 }}>{error}</Alert>}
      {loading ? <Stack alignItems="center" spacing={2} sx={{ py: 9 }}><CircularProgress sx={{ color: '#f59e0b' }} /><Typography color="text.secondary">Preparing day plans…</Typography></Stack> : <>
        <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0' }}><Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}><Typography variant="h6" fontWeight={900}>{currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</Typography><Stack direction="row"><IconButton onClick={() => setCurrentMonth((month) => new Date(month.getFullYear(), month.getMonth() - 1, 1))}><ChevronLeftIcon /></IconButton><IconButton onClick={() => setCurrentMonth((month) => new Date(month.getFullYear(), month.getMonth() + 1, 1))}><ChevronRightIcon /></IconButton></Stack></Stack>
          {calendarLoading ? <Stack alignItems="center" sx={{ py: 7 }}><CircularProgress size={30} sx={{ color: '#f59e0b' }} /></Stack> : <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderTop: '1px solid #cbd5e1', borderLeft: '1px solid #cbd5e1' }}>{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((day) => <Box key={day} sx={{ p: 1, textAlign: 'center', bgcolor: '#1e293b', color: '#fff', fontWeight: 900, fontSize: 12 }}>{day}</Box>)}{calendarDays.map(({ date, dateKey, inMonth, data: calendarDay }) => { const holiday = isHolidayRow(calendarDay); const planned = isPlannedRow(calendarDay); const title = holiday ? valueOf(calendarDay, ['holidayName','HolidayName'], 'Holiday') : valueOf(calendarDay, ['dayTitle','DayTitle'], ''); const dayNo = valueOf(calendarDay, ['dayNo','DayNo'], ''); return <Box key={dateKey} sx={{ minHeight: 105, p: 1, borderRight: '1px solid #cbd5e1', borderBottom: '1px solid #cbd5e1', borderLeft: holiday ? '4px solid #e11d48' : planned ? '4px solid #f59e0b' : 0, bgcolor: holiday ? '#fff1f2' : planned ? '#fffbeb' : inMonth ? '#fff' : '#f1f5f9', color: inMonth ? '#0f172a' : '#94a3b8' }}><Typography variant="caption" fontWeight={900}>{date.getDate()}</Typography>{dayNo !== '' && <Typography variant="caption" display="block" sx={{ mt: .5, color: '#b45309', fontWeight: 900 }}>Day {dayNo}</Typography>}{title && <Typography variant="caption" display="block" sx={{ mt: .25, fontWeight: 800, lineHeight: 1.25, color: holiday ? '#be123c' : '#334155' }}>{title}</Typography>}</Box>; })}</Box>}
        </Box><TableContainer sx={{ maxHeight: 540 }}><Table stickyHeader size="small"><TableHead><TableRow>{columns.map(([key, label]) => <TableCell key={key} sx={{ bgcolor: '#1e293b', color: '#fff', fontWeight: 900 }}>{label}</TableCell>)}</TableRow></TableHead><TableBody>{rows.length ? rows.map((row) => <TableRow hover key={row.id}>{columns.map(([key, , type]) => <TableCell key={key}>{type === 'date' ? formatDate(row[key]) : row[key]}</TableCell>)}</TableRow>) : <TableRow><TableCell colSpan={columns.length} align="center" sx={{ py: 7, color: 'text.secondary' }}>{projectId && projectPlanId ? 'No day plans available for this selection.' : 'Select a project and project plan to view the report.'}</TableCell></TableRow>}</TableBody></Table></TableContainer></>}
    </Paper>
  </PageShell>;
}
